import {
  AmountCell,
  DateCell,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  type BaseExpense,
  ExpenseStatusEnum,
  getExpensesDates,
} from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { ExpenseCategory } from '../../expense-category';
import { ExpenseStatus } from '../../expense-status';

export enum ExpensesColumnHeaders {
  employeeName = 'Employee',
  spentAt = 'Spent at',
  amount = 'Amount',
  dateSpent = 'Date spent',
  appover = 'Approver',
  status = 'Status',
}

export const useExpensesTableConfig = ({
  columnsOverride,
}: {
  columnsOverride?: ColumnDef<BaseExpense, unknown>[];
}) => {
  const columnHelper = createColumnHelper<BaseExpense>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<BaseExpense, any>[] = useMemo(
    () => [
      columnHelper.accessor('employeeName', {
        cell: (info) => info?.getValue() ?? '',
        header: () => ExpensesColumnHeaders.employeeName,
      }),
      columnHelper.accessor('title', {
        cell: (info) => (
          <Stack gap="4" w={250}>
            {info?.row.original.title && (
              <Typography as="span" size="14" weight="medium">
                {info?.row.original.title}
              </Typography>
            )}
            {info?.row.original.category && (
              <ExpenseCategory category={info?.row.original.category} />
            )}
          </Stack>
        ),
        header: () => ExpensesColumnHeaders.spentAt,
      }),
      columnHelper.accessor('amount', {
        cell: (info) => (
          <AmountCell
            amount={info.row.original.amount.amount}
            currency={info.row.original.amount.currency}
          />
        ),
        header: () => ExpensesColumnHeaders.amount,
      }),
      columnHelper.accessor('dateSpent', {
        cell: (info) => <DateCell value={info.getValue()} utc />,
        header: () => ExpensesColumnHeaders.dateSpent,
      }),
      columnHelper.accessor('approverName', {
        cell: (info) => info?.row.original.approverName ?? '',
        header: () => ExpensesColumnHeaders.appover,
      }),
      columnHelper.accessor('status', {
        cell: (info) => {
          const status = info?.row.original.status;
          const { approvedDate, reportedDate } = getExpensesDates(
            info?.row.original.statusHistory ?? [],
          );
          return (
            <Stack gap="4">
              <ExpenseStatus status={status} />
              {status === ExpenseStatusEnum.APPROVED && approvedDate && (
                <DateCell value={approvedDate} size="12" color="subtle" />
              )}
              {status === ExpenseStatusEnum.PROCESSED && reportedDate && (
                <DateCell value={reportedDate} size="12" color="subtle" />
              )}
            </Stack>
          );
        },
        header: () => ExpensesColumnHeaders.status,
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride],
  );

  return {
    columns,
    columnHelper,
  };
};
